import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ClipboardList,
  PackageOpen,
  Warehouse,
} from 'lucide-react';

import { KpiCard } from '@/components/shared/KpiCard';
import type { NetworkReplenishmentDashboardKpis } from '@/services/networkReplenishmentService';

export type NetworkKpiFilterKey =
  | 'to_review'
  | 'approved_today'
  | 'sent_today'
  | 'errors'
  | 'upcoming'
  | null;

interface NetworkReplenishmentKpiCardsProps {
  kpis: NetworkReplenishmentDashboardKpis | null;
  loading?: boolean;
  activeFilter?: NetworkKpiFilterKey;
  onFilter?: (key: NetworkKpiFilterKey) => void;
}

const iconClass = 'h-5 w-5 text-[#1681ba]';

function num(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function NetworkReplenishmentKpiCards({
  kpis,
  loading = false,
  activeFilter = null,
  onFilter,
}: NetworkReplenishmentKpiCardsProps) {
  const { t } = useTranslation();

  const click = (key: NetworkKpiFilterKey) => {
    if (!onFilter) return;
    onFilter(activeFilter === key ? null : key);
  };

  const toReview = kpis?.transfers_to_review ?? 0;
  const units = num(kpis?.units_to_transfer);
  const upcoming = kpis?.upcoming_shipments ?? 0;
  const approvedToday = kpis?.transfers_approved_today ?? 0;
  const sentToday = kpis?.transfers_sent_today ?? 0;
  const errors = kpis?.transfers_with_error ?? 0;
  const incomplete = kpis?.products_incomplete_allocation ?? 0;
  const atRisk = kpis?.stores_at_stockout_risk ?? 0;
  const issues = errors + incomplete + atRisk;
  const committed = num(kpis?.central_committed_stock);

  const subtitleClass =
    'mt-1.5 max-w-full px-1 text-center text-xs leading-snug text-gray-500 break-words';

  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div className="min-w-0">
        <KpiCard
          title={t('networkReplenishment.kpis.toReview')}
          value={kpis ? toReview : '—'}
          isCount
          loading={loading}
          helpText={t('networkReplenishment.kpis.toReviewHelp')}
          icon={<ClipboardList className={iconClass} />}
          variant={toReview > 0 ? 'warning' : 'default'}
          subtitle={
            kpis
              ? t('networkReplenishment.kpis.toReviewSubtitle', {
                  units: units.toLocaleString(),
                })
              : undefined
          }
          subtitleClassName={subtitleClass}
          onClick={() => click('to_review')}
        />
      </div>
      <div className="min-w-0">
        <KpiCard
          title={t('networkReplenishment.kpis.pipeline')}
          value={kpis ? upcoming : '—'}
          isCount
          loading={loading}
          helpText={t('networkReplenishment.kpis.pipelineHelp')}
          icon={<PackageOpen className={iconClass} />}
          valueNote={t('networkReplenishment.kpis.pipelineNote')}
          subtitle={
            kpis
              ? t('networkReplenishment.kpis.pipelineSubtitle', {
                  approved: approvedToday,
                  sent: sentToday,
                })
              : undefined
          }
          subtitleClassName={subtitleClass}
          onClick={() => click('upcoming')}
        />
      </div>
      <div className="min-w-0">
        <KpiCard
          title={t('networkReplenishment.kpis.issues')}
          value={kpis ? issues : '—'}
          isCount
          loading={loading}
          helpText={t('networkReplenishment.kpis.issuesHelp')}
          icon={<AlertTriangle className={iconClass} />}
          variant={errors > 0 || atRisk > 0 ? 'danger' : issues > 0 ? 'warning' : 'default'}
          subtitle={
            kpis
              ? t('networkReplenishment.kpis.issuesSubtitle', {
                  errors,
                  incomplete,
                  atRisk,
                })
              : undefined
          }
          subtitleClassName={subtitleClass}
          onClick={() => click('errors')}
        />
      </div>
      <div className="min-w-0">
        <KpiCard
          title={t('networkReplenishment.kpis.centralCommitted')}
          value={kpis ? committed : '—'}
          isCount
          loading={loading}
          helpText={t('networkReplenishment.kpis.centralCommittedHelp')}
          valueNote={t('networkReplenishment.kpis.unitsNote')}
          icon={<Warehouse className={iconClass} />}
        />
      </div>
    </div>
  );
}
